import { useNavigate } from 'react-router-dom';
import { Sword, Shield } from 'lucide-react';

export default function BetaGatePage({ pending = false }) {
  const navigate = useNavigate();

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0a0a14 0%, #0d1117 40%, #0a0e08 100%)' }}
      data-testid="beta-gate-page"
    >
      {/* Ambient texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Radial vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.75) 100%)',
        }}
      />

      {/* Decorative rune border — top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-lg">

        {/* Emblem */}
        <div className="mb-8 relative">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center border-2 border-amber-600/40"
            style={{ background: 'radial-gradient(circle, rgba(180,120,20,0.15) 0%, rgba(180,120,20,0.03) 100%)' }}
          >
            <Shield className="w-10 h-10 text-amber-500/80" />
          </div>
          <div className="absolute -inset-2 rounded-full border border-amber-700/20" />
          <div className="absolute -inset-5 rounded-full border border-amber-800/10" />
        </div>

        {/* Brand */}
        <p className="text-amber-500/60 text-xs uppercase tracking-[0.35em] mb-2 font-mono">
          ᛗᛁᛞᚷᚨᚱᛞ
        </p>
        <h1
          className="text-5xl sm:text-6xl font-bold text-amber-100 mb-3 leading-none"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '-0.01em' }}
        >
          Midgard
        </h1>
        <p className="text-amber-400/80 text-lg sm:text-xl font-light tracking-wide mb-10">
          The Realms Are Awakening
        </p>

        {/* Divider rune strip */}
        <div className="flex items-center gap-3 w-full mb-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-700/40" />
          <Sword className="w-4 h-4 text-amber-600/50 shrink-0" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-700/40" />
        </div>

        {/* Body */}
        {pending ? (
          <>
            <p className="text-amber-100/70 text-base leading-relaxed mb-3">
              Your account exists, but has not yet been granted passage into the private beta.
            </p>
            <p className="text-amber-400/50 text-sm leading-relaxed mb-10">
              The Allfather watches your request. You will be summoned when the gates open.
            </p>
          </>
        ) : (
          <>
            <p className="text-amber-100/70 text-base leading-relaxed mb-3">
              Midgard is in private beta. The gates are not yet open to all who seek entry.
            </p>
            <p className="text-amber-400/50 text-sm leading-relaxed mb-10">
              Only those chosen by the Allfather may pass. The realms stir in preparation.
            </p>
          </>
        )}

        {/* Actions */}
        {pending ? (
          <button
            onClick={() => {
              localStorage.removeItem('midgard_token');
              window.location.reload();
            }}
            className="px-6 py-2.5 rounded-xl text-sm text-amber-400/60 border border-amber-800/30 hover:border-amber-600/40 hover:text-amber-300/80 transition-all"
          >
            Sign out
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-3 rounded-xl text-sm font-semibold text-black bg-amber-500 hover:bg-amber-400 transition-all shadow-[0_0_24px_rgba(245,158,11,0.25)]"
            >
              Sign In
            </button>
            <a
              href="mailto:johannesmidgardr@gmail.com?subject=Midgard%20Beta%20Access%20Request"
              className="px-8 py-3 rounded-xl text-sm font-semibold text-amber-300/80 border border-amber-700/40 hover:border-amber-500/60 hover:text-amber-200 transition-all"
            >
              Request Access
            </a>
          </div>
        )}

        {/* Footer */}
        <p className="mt-12 text-amber-800/50 text-[11px] uppercase tracking-widest">
          Private Beta · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
